'use client';

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePathname } from 'next/navigation';
import { pathToRegexp } from 'path-to-regexp';

import { getClientEnv } from '@/config/clientEnv';
import { AUTHENTICATION_ROUTES, PRIVATE_ROUTES } from '@/config/routes';
import { generateApproperiateURL, getValidSubdomain } from '@/helpers/utils/urlHelpers';
import { useAppDispatch, useAppSelector } from '@/store';
import { setAuthenticationLoadingStatus, setAuthenticationStatus } from '@/store/slices/authentication';
import { RefreshTokenData } from '@/types/Signup';
import { SuccessResponse } from '@/types/Success';

import { isRegExp } from '../helpers/utils/typeGaurds';

export default function RevalidateAuthStatus({
  children, // isPrivateRoute,
}: {
  children: ReactNode;
  isPrivateRoute: boolean;
}) {
  const webalias = useAppSelector(store => store.slices.webaliasSlice.webalias);
  const isWebaliasLoading = useAppSelector(store => store.slices.webaliasSlice.isLoading);
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [loadingTime, setLoadingTime] = useState(0);

  const isAuthLoading = useAppSelector(store => store.slices.authenticationSlice.isAuthenticationLoading);

  const subdomain = useMemo(() => {
    return getValidSubdomain(globalThis?.window?.location?.hostname);
  }, []);

  const requestUrl = useMemo(() => {
    return pathname;
  }, [pathname]);

  const isPrivateRoute = useMemo(() => {
    let isPrivate = false;

    const isBackOffice = subdomain === 'backoffice' || subdomain?.includes('backoffice');

    const urlToTest = requestUrl.split('?')[0];

    isPrivate = PRIVATE_ROUTES.map(route => {
      try {
        return pathToRegexp(route);
      } catch (err) {
        return route;
      }
    }).some(rx => (isRegExp(rx) ? rx.test(urlToTest) : rx === urlToTest));
    const isBackofficePublicRoute =
      urlToTest === '/login' ||
      urlToTest.startsWith('/login?') ||
      urlToTest.startsWith('/login/?') ||
      urlToTest === '/forgotpassword' ||
      urlToTest.startsWith('/forgotpassword') ||
      urlToTest?.toLowerCase().startsWith('/adminlogin') ||
      urlToTest?.toLowerCase() === '/adminlogin' ||
      urlToTest?.toLowerCase().startsWith('/silentlogin') ||
      urlToTest?.toLowerCase() === '/silentlogin' ||
      urlToTest?.toLowerCase().startsWith('/logout') ||
      urlToTest?.toLowerCase() === '/logout';

    return !!(isPrivate || (isBackOffice && !isBackofficePublicRoute));
  }, [subdomain, requestUrl, pathname]);

  const isAuthenticationRoute = useMemo(() => {
    let isAuthRoute = false;
    const urlToTest = requestUrl.split('?')[0];
    isAuthRoute = AUTHENTICATION_ROUTES.map(route => {
      try {
        return pathToRegexp(route);
      } catch (err) {
        return route;
      }
    }).some(rx => (isRegExp(rx) ? rx.test(urlToTest) : rx === urlToTest));
    return !!isAuthRoute;
  }, [requestUrl]);

  const redirectToHomepage = useCallback(() => {
    const { IS_BACKOFFICE, WEBALIAS } = getClientEnv();

    if (IS_BACKOFFICE) {
      window.location.href = `/`;
      return;
    }

    window.location.href = generateApproperiateURL(`/`, webalias || WEBALIAS || '');
  }, [webalias]);

  const redirectToLogin = useCallback(() => {
    const { IS_BACKOFFICE, WEBALIAS } = getClientEnv();

    const isGuestUserFlow =
      typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('isGuestUser') === 'true';

    if (IS_BACKOFFICE) {
      window.location.href = `/login?ReturnUrl=${encodeURIComponent(window.location.pathname)}`;
      return;
    }

    if (isGuestUserFlow) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('isGuestUser');
      const cleanReturnUrl = currentUrl.toString();

      window.location.href = generateApproperiateURL(
        `/account/login?tab=signup&ReturnUrl=${encodeURIComponent(cleanReturnUrl)}`,
        webalias || WEBALIAS || ''
      );
      return;
    }

    window.location.href = generateApproperiateURL(
      `/account/login/?ReturnUrl=${encodeURIComponent(window.location.href)}`,
      webalias || WEBALIAS || ''
    );
  }, [webalias]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const revalidateAuthStatus = useCallback(async () => {
    // if (!isAuthenticationRoute && !isPrivateRoute) return;

    const { generateAuthRefreshURL, generateAuthURL, HOST, ORIGIN } = getClientEnv();
    console.log('[Auth Debug] setting authentication loading status to true');
    dispatch(setAuthenticationLoadingStatus(true));

    if (!HOST || !ORIGIN) {
      console.warn('[Auth Debug] HOST or ORIGIN is not defined - queuing revalidateAuthStatus');
      timeoutRef.current = setTimeout(() => {
        console.log('[Auth Debug] calling revalidateAuthStatus again');
        revalidateAuthStatus();
        clearTimeout(timeoutRef.current!);
      }, 500);
      return;
    }

    clearTimeout(timeoutRef.current!);

    const AUTH_URL = generateAuthURL(),
      AUTH_REFRESH_URL = generateAuthRefreshURL();

    try {
      console.log('[Auth Debug] making request to AUTH_URL:', AUTH_URL);
      const controller = new AbortController();
      const signal = controller.signal;

      let resp = await fetch(AUTH_URL, {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        credentials: 'same-origin',
        cache: 'no-store',
        method: 'GET',
        headers: {
          __origin: ORIGIN,
          __host: HOST,
          HOST,
          ORIGIN,
          ...(window?.__FORWARDED_HEADERS__ ? window.__FORWARDED_HEADERS__ : {}),
        },
        signal,
      });

      if (resp.status === 401) {
        console.log('[Auth Debug] received 401 response - attempting to refresh authentication');
        const refreshResp = await fetch(AUTH_REFRESH_URL, {
          method: 'POST',
          // eslint-disable-next-line sonarjs/no-duplicate-string
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            __origin: ORIGIN,
            __host: HOST,
            HOST,
            ORIGIN,
            'Content-Type': 'application/json',
            ...(window?.__FORWARDED_HEADERS__ ? window.__FORWARDED_HEADERS__ : {}),
          },
        });

        const refreshData = (await refreshResp.json()) as SuccessResponse<RefreshTokenData>;

        if (refreshData.success && refreshData.data) {
          dispatch(setAuthenticationStatus(true));
          dispatch(setAuthenticationLoadingStatus(false));
          return;
        }

        console.log('[Auth Debug] refresh attempt complete - token should be refreshed');
        console.log('[Auth Debug] retrying original AUTH_URL after refresh');
        resp = await fetch(AUTH_URL, {
          // eslint-disable-next-line sonarjs/no-duplicate-string
          credentials: 'same-origin',
          cache: 'no-store',
          method: 'GET',
          headers: {
            __origin: ORIGIN,
            __host: HOST,
            HOST,
            ORIGIN,
            ...(window?.__FORWARDED_HEADERS__ ? window.__FORWARDED_HEADERS__ : {}),
          },
        });

        console.log('[Auth Debug] retried AUTH_URL, status:', resp.status);
      }

      let data = (await resp.json()) as SuccessResponse<unknown>;

      console.log('[Auth Debug] authentication check response data:', data);

      dispatch(setAuthenticationStatus(data.success));

      if (isAuthenticationRoute && data.success) return redirectToHomepage();
      if (isPrivateRoute && !data.success) return redirectToLogin();
    } catch (error) {
      console.error('[Auth Debug] error while checking the auth status', (error as Error)?.message, error);

      if (isPrivateRoute) return redirectToLogin();
    }
    dispatch(setAuthenticationLoadingStatus(false));
    console.log('[Auth Debug] FINAL setting authentication loading status to false');
  }, [dispatch, isPrivateRoute, redirectToLogin, isAuthenticationRoute, redirectToHomepage]);

  useEffect(() => {
    const { IS_BACKOFFICE } = getClientEnv();

    if (!isWebaliasLoading || IS_BACKOFFICE) revalidateAuthStatus();
  }, [dispatch, isWebaliasLoading, revalidateAuthStatus, isPrivateRoute]);

  // Timer effect to track loading time
  useEffect(() => {
    if (isPrivateRoute && isAuthLoading) {
      // Start the timer when loading begins
      setLoadingTime(0);
      timerRef.current = setInterval(() => {
        setLoadingTime(prev => prev + 1);
      }, 1000);
    } else {
      // Clear the timer when loading ends
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPrivateRoute, isAuthLoading]);

  const formatTime = (seconds: number) => {
    try {
      if (globalThis?.window?.FS) {
        globalThis?.window?.FS?.event?.('AuthenticationLoading', {
          seconds: `${seconds}s`,
          secondsNumber: seconds,
        });

        globalThis?.window?.FS?.event?.(`AuthenticationLoading_${seconds}s`, {
          seconds: `${seconds}s`,
          secondsNumber: seconds,
        });
      }
    } catch (error) {}

    return null;
  };

  if (isPrivateRoute && isAuthLoading) {
    return <>{formatTime(loadingTime)}</>;
  }

  if (isAuthenticationRoute && isAuthLoading) {
    return <>{formatTime(loadingTime)}</>;
  }

  return <>{children}</>;
}
